import * as XLSX from "xlsx";
import { formatDate, formatDateTime } from "@/lib/utils";
import { isNumericColumn, roundTo, type CellValue, type ReportColumn } from "./columns";

export type SheetRows = (string | number)[][];

/** Header + one row per record (+ an optional totals row); numbers stay numeric so Excel can sum them. */
export function rowsToSheet<Row>(
	columns: ReportColumn<Row>[],
	rows: Row[],
	totals?: { label: string; totals: Record<string, number> },
): SheetRows {
	const out: SheetRows = [columns.map(c => c.label)];
	for (const row of rows) out.push(columns.map(c => exportValue(c, c.value(row))));
	if (totals) {
		out.push(
			columns.map((c, i) =>
				c.key in totals.totals ? exportValue(c, totals.totals[c.key]) : i === 0 ? totals.label : "",
			),
		);
	}
	return out;
}

/** rowsToSheet with a totals row summing every column marked `total`. */
export function sheetWithTotals<Row>(columns: ReportColumn<Row>[], rows: Row[], totalLabel: string): SheetRows {
	const totals: Record<string, number> = {};
	for (const col of columns) {
		if (col.total) totals[col.key] = rows.reduce((sum, row) => sum + (Number(col.value(row)) || 0), 0);
	}
	return rowsToSheet(columns, rows, Object.keys(totals).length ? { label: totalLabel, totals } : undefined);
}

function exportValue<Row>(col: ReportColumn<Row>, value: CellValue): string | number {
	if (value === null || value === undefined || value === "") return "";
	if (isNumericColumn(col)) return roundTo(Number(value) || 0, 2);
	if (col.kind === "date") return formatDate(value);
	if (col.kind === "datetime") return formatDateTime(value);
	return String(value);
}

/** Excel limits sheet names to 31 characters and forbids []:*?/\ . */
export function safeSheetName(name: string): string {
	return (
		name
			.replace(/[[\]:*?/\\]/g, " ")
			.slice(0, 31)
			.trim() || "Sheet"
	);
}

/** Writes and downloads an .xlsx; Arabic workbooks open right-to-left. */
export function downloadWorkbook(sheets: { name: string; rows: SheetRows }[], fileName: string, language?: string) {
	const wb = XLSX.utils.book_new();
	const used = new Set<string>();
	for (const sheet of sheets) {
		let name = safeSheetName(sheet.name);
		for (let i = 2; used.has(name); i++) name = safeSheetName(`${sheet.name.slice(0, 26)} (${i})`);
		used.add(name);
		const ws = XLSX.utils.aoa_to_sheet(sheet.rows);
		ws["!cols"] = (sheet.rows[0] ?? []).map((_, col) => ({
			wch: Math.min(40, Math.max(10, ...sheet.rows.map(r => String(r[col] ?? "").length + 2))),
		}));
		XLSX.utils.book_append_sheet(wb, ws, name);
	}
	if (language?.startsWith("ar")) wb.Workbook = { Views: [{ RTL: true }] };
	const safeFile = fileName.replace(/[\\/:*?"<>|]+/g, "-").trim() || "report";
	XLSX.writeFile(wb, `${safeFile}.xlsx`);
}
